/**
 * Produit scalaire — calculs purs sur des vecteurs à coordonnées entières.
 *
 * u · v = xx′ + yy′ (exact), = ‖u‖ × ‖v‖ × cos θ, = ‖u‖ × OH où H est le
 * projeté orthogonal de l'extrémité de v sur la droite portée par u (OH
 * compté positivement dans le sens de u).
 */

import type { Vec } from '../vector-sum/vectors';

export type { Vec };

export function dot(u: Vec, v: Vec): number {
  return u.x * v.x + u.y * v.y;
}

export function norm2(u: Vec): number {
  return u.x * u.x + u.y * u.y;
}

/** angle (u, v) en degrés, entre 0 et 180 */
export function angleDeg(u: Vec, v: Vec): number {
  const n = Math.sqrt(norm2(u) * norm2(v));
  if (!n) return 0;
  const c = Math.max(-1, Math.min(1, dot(u, v) / n));
  return (Math.acos(c) * 180) / Math.PI;
}

/** projeté de v sur la droite (O, u) et sa mesure algébrique OH (sens de u) */
export function projection(u: Vec, v: Vec): { h: Vec; oh: number } {
  const n2 = norm2(u);
  if (!n2) return { h: { x: 0, y: 0 }, oh: 0 };
  const k = dot(u, v) / n2;
  return { h: { x: k * u.x, y: k * u.y }, oh: dot(u, v) / Math.sqrt(n2) };
}

export type Sign = 'positive' | 'zero' | 'negative';

export function sign(u: Vec, v: Vec): Sign {
  const d = dot(u, v);
  return d > 0 ? 'positive' : d < 0 ? 'negative' : 'zero';
}

export interface DotGoal {
  id: string;
  text: string;
  check: (u: Vec, v: Vec) => boolean;
}

const tilted = (w: Vec) => w.x !== 0 && w.y !== 0;

export const GOALS: readonly DotGoal[] = [
  {
    id: 'orthogonal',
    text: 'Rends u et v orthogonaux, sans qu’aucun ne soit horizontal ni vertical.',
    check: (u, v) => dot(u, v) === 0 && tilted(u) && tilted(v),
  },
  {
    id: 'minus-6',
    text: 'Obtiens u · v = −6.',
    check: (u, v) => dot(u, v) === -6,
  },
  {
    id: 'max',
    text: 'Obtiens u · v = ‖u‖ × ‖v‖ : quand est-ce possible ?',
    check: (u, v) => norm2(u) > 0 && norm2(v) > 0 && dot(u, v) > 0 && u.x * v.y - u.y * v.x === 0,
  },
];
